/**
 * Optional PAN card / GST certificate for a CP application or profile.
 *
 * Either reuse a confirmed PAN/GST file already on the account, or upload a
 * new one (initiate → presigned PUT → confirm). Only a CONFIRMED id is ever
 * handed to the form. Removing detaches the file; it does not delete it.
 */
import * as React from 'react';
import { Eye, FileBadge, Loader2, Upload, X } from 'lucide-react';
import { isApiError } from '@finmin/api-client';
import { formatDate } from '@finmin/utils';
import { Button, ErrorMessage, FancySelect, Progress } from '@finmin/ui';
import {
  CHANNEL_PARTNER_UPLOAD_ACCEPT,
  uploadChannelPartnerDocument,
  useReusableChannelPartnerDocuments,
  type ChannelPartnerDocumentType,
  type ChannelPartnerReusableDocument,
} from '@/api/channel-partners';
import { openChannelPartnerDocument } from '../open-document';

const MAX_BYTES = 20 * 1024 * 1024;

const MATCHING_TYPES: Record<ChannelPartnerDocumentType, readonly string[]> = {
  pan: ['pan', 'cp_pan'],
  gst: ['gst', 'cp_gst'],
};

const TYPE_NAMES: Record<ChannelPartnerDocumentType, string> = {
  pan: 'PAN card',
  gst: 'GST certificate',
};

function describe(doc: ChannelPartnerReusableDocument, type: ChannelPartnerDocumentType): string {
  return `${doc.document_label?.trim() || TYPE_NAMES[type]} · ${formatDate(doc.created_at)}`;
}

export function DocumentAttachmentField({
  documentType,
  value,
  onChange,
  disabled,
}: {
  documentType: ChannelPartnerDocumentType;
  value: string | null;
  onChange: (documentId: string | null) => void;
  disabled?: boolean;
}) {
  const reusable = useReusableChannelPartnerDocuments();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const matching = (reusable.data ?? []).filter((d) =>
    MATCHING_TYPES[documentType].includes(d.document_type),
  );
  const attached = value ? (reusable.data ?? []).find((d) => d.document_id === value) : undefined;
  const uploading = progress !== null;

  async function handleFile(file: File) {
    setError(null);
    if (file.size > MAX_BYTES) {
      setError('Files must be 20 MB or smaller.');
      return;
    }
    setProgress(0);
    try {
      onChange(await uploadChannelPartnerDocument(file, documentType, setProgress));
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Upload failed. Please try again.');
    } finally {
      setProgress(null);
    }
  }

  if (uploading) {
    return (
      <div className="space-y-2 rounded-md border border-border bg-card p-3" aria-live="polite">
        <p className="flex items-center gap-2 text-sm text-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {progress < 100 ? `Uploading ${TYPE_NAMES[documentType]}… ${progress}%` : 'Confirming upload…'}
        </p>
        <Progress value={progress} className="h-2" />
      </div>
    );
  }

  if (value) {
    return (
      <div className="space-y-2">
        <div className="space-y-1 rounded-md border border-border bg-card p-3">
          {/* Label on its own line: the field often sits in a half-width column. */}
          <p className="flex items-start gap-2 text-sm text-foreground">
            <FileBadge className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">
              {attached ? describe(attached, documentType) : `${TYPE_NAMES[documentType]} attached`}
            </span>
          </p>
          <div className="flex flex-wrap gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => void openChannelPartnerDocument(value)}>
              <Eye className="h-3.5 w-3.5" aria-hidden /> View
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => onChange(null)}
              aria-label={`Remove ${TYPE_NAMES[documentType]}`}
            >
              <X className="h-3.5 w-3.5" aria-hidden /> Remove
            </Button>
          </div>
        </div>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" aria-hidden /> Upload {TYPE_NAMES[documentType]}
        </Button>
        {matching.length > 0 && (
          <>
            <span className="text-xs text-muted-foreground sm:px-1">or</span>
            <div className="min-w-0 flex-1">
              <FancySelect
                options={matching.map((d) => ({ value: d.document_id, label: describe(d, documentType) }))}
                value=""
                onChange={(id) => {
                  setError(null);
                  onChange(id);
                }}
                disabled={disabled}
                placeholder="Use a file already on your account"
                ariaLabel={`Use an existing ${TYPE_NAMES[documentType]}`}
              />
            </div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={CHANNEL_PARTNER_UPLOAD_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      <p className="text-[11px] text-muted-foreground">PDF, JPG or PNG, up to 20 MB.</p>
      <ErrorMessage message={error} />
    </div>
  );
}
